import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import {
  AdminBattlePassLevel,
  AdminBattlePassMission,
  AdminBattlePassReward,
  AdminSeasonOverview,
  AdminSeasonReward,
} from '../../../models/season-admin';
import { SeasonAdminService } from '../../../services/season-admin.service';
import { AdminPageHeaderComponent } from '../../shared/admin-page-header/admin-page-header.component';

@Component({
  selector: 'app-season-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, DatePipe, AdminPageHeaderComponent],
  templateUrl: './season-detail.component.html',
  styleUrl: './season-detail.component.scss',
})
export class SeasonDetailComponent implements OnInit {
  seasonId!: number;
  season?: AdminSeasonOverview;
  templates: AdminSeasonOverview[] = [];

  isLoading = false;
  feedbackError = '';
  feedbackSuccess = '';

  seasonName = '';
  seasonStartLocal = '';
  seasonEndLocal = '';

  passName = '';
  passDescription = '';
  passPriceGems = 500;
  passPriceCoins = 2000;
  passLevelDecoratorImage: File | null = null;
  passBackgroundImage: File | null = null;
  passRewardsPreviewImage: File | null = null;
  passBuyModalImage: File | null = null;

  cloneTemplateSeasonId?: number;
  cloneMissions = true;
  cloneLevels = true;
  cloneRewards = true;
  replaceExisting = true;

  seasonRewards: AdminSeasonReward[] = [];
  rewardForm: Partial<AdminSeasonReward> = { position: 1, reward: '' };
  editingSeasonRewardId?: number;

  levels: AdminBattlePassLevel[] = [];
  levelForm: Partial<AdminBattlePassLevel> = { level: 0, xp_to_unlock: 0 };
  editingLevelId?: number;

  missions: AdminBattlePassMission[] = [];
  missionForm: Partial<AdminBattlePassMission> = {
    category: 'BATTLE_VOLUME',
    name: '',
    description: '',
    xp_reward: 0,
    target_value: 1,
    query: '',
  };
  missionActivationLocal = '';
  editingMissionId?: number;

  passRewards: AdminBattlePassReward[] = [];
  passRewardForm = {
    level: 0,
    reward_type: 'COIN',
    category: 'FREE',
    reward_amount: 0,
    hero_id: null as number | null,
  };
  editingPassRewardId?: number;

  missionCategories = [
    'BATTLE_VOLUME',
    'STAKE_VOLUME',
    'PURCHASE_VOLUME',
    'STRATEGIC_TIME',
    'SCORE',
    'GEM_CONVERSION',
  ];

  constructor(private route: ActivatedRoute, private seasonService: SeasonAdminService) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (!idParam) {
      return;
    }
    this.seasonId = Number(idParam);
    this.loadAll();
  }

  loadAll(): void {
    this.isLoading = true;
    this.feedbackError = '';
    this.feedbackSuccess = '';

    this.seasonService.detail(this.seasonId).subscribe({
      next: (season) => {
        this.season = season;
        this.seasonName = season.name;
        this.seasonStartLocal = this.toLocalDateInput(season.start_date);
        this.seasonEndLocal = this.toLocalDateInput(season.end_date);
        this.passName = season.battle_pass?.name || '';
        this.passDescription = season.battle_pass?.description || '';
        this.passPriceGems = season.battle_pass?.price_gems || 500;
        this.passPriceCoins = season.battle_pass?.price_coins || 2000;
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível carregar a temporada.');
      },
    });

    this.seasonService.templates(this.seasonId).subscribe({
      next: (response) => {
        this.templates = response || [];
      },
      error: () => {
        this.templates = [];
      },
    });

    this.seasonService.listSeasonRewards(this.seasonId).subscribe({
      next: (response) => (this.seasonRewards = response || []),
      error: () => (this.seasonRewards = []),
    });

    this.seasonService.listLevels(this.seasonId).subscribe({
      next: (response) => (this.levels = response || []),
      error: () => (this.levels = []),
    });

    this.seasonService.listMissions(this.seasonId).subscribe({
      next: (response) => (this.missions = response || []),
      error: () => (this.missions = []),
    });

    this.seasonService.listPassRewards(this.seasonId).subscribe({
      next: (response) => {
        this.passRewards = response || [];
        this.isLoading = false;
      },
      error: () => {
        this.passRewards = [];
        this.isLoading = false;
      },
    });
  }

  updateSeason(): void {
    if (!this.seasonName || !this.seasonStartLocal || !this.seasonEndLocal) {
      this.feedbackError = 'Preencha nome, início e fim da temporada.';
      return;
    }

    this.seasonService
      .manageSeason(this.seasonId, {
        name: this.seasonName.trim(),
        start_date: new Date(this.seasonStartLocal).toISOString(),
        end_date: new Date(this.seasonEndLocal).toISOString(),
      })
      .subscribe({
        next: () => {
          this.feedbackSuccess = 'Temporada atualizada com sucesso.';
          this.loadAll();
        },
        error: (error) => {
          this.feedbackError = this.extractErrorMessage(error, 'Não foi possível atualizar a temporada.');
        },
      });
  }

  updateBattlePass(): void {
    if (!this.passName) {
      this.feedbackError = 'Informe o nome do passe.';
      return;
    }

    const formData = new FormData();
    formData.append('name', this.passName.trim());
    formData.append('description', this.passDescription.trim());
    formData.append('price_gems', String(this.passPriceGems));
    formData.append('price_coins', String(this.passPriceCoins));

    if (this.passLevelDecoratorImage) {
      formData.append('level_decorator_image', this.passLevelDecoratorImage);
    }
    if (this.passBackgroundImage) {
      formData.append('background_image', this.passBackgroundImage);
    }
    if (this.passRewardsPreviewImage) {
      formData.append('rewards_preview_image', this.passRewardsPreviewImage);
    }
    if (this.passBuyModalImage) {
      formData.append('buy_modal_background_image', this.passBuyModalImage);
    }

    this.seasonService.manageBattlePass(this.seasonId, formData).subscribe({
      next: () => {
        this.feedbackSuccess = 'Passe de batalha atualizado com sucesso.';
        this.loadAll();
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível atualizar o passe.');
      },
    });
  }

  cloneFromTemplate(): void {
    if (!this.cloneTemplateSeasonId) {
      this.feedbackError = 'Selecione uma temporada template.';
      return;
    }

    this.seasonService
      .cloneBattlePass(this.seasonId, {
        template_season_id: this.cloneTemplateSeasonId,
        clone_missions: this.cloneMissions,
        clone_levels: this.cloneLevels,
        clone_rewards: this.cloneRewards,
        replace_existing: this.replaceExisting,
      })
      .subscribe({
        next: () => {
          this.feedbackSuccess = 'Clone do passe executado com sucesso.';
          this.loadAll();
        },
        error: (error) => {
          this.feedbackError = this.extractErrorMessage(error, 'Não foi possível clonar o passe.');
        },
      });
  }

  saveSeasonReward(): void {
    if (!this.rewardForm.position || !this.rewardForm.reward) {
      this.feedbackError = 'Informe posição e recompensa da temporada.';
      return;
    }

    const payload = {
      position: Number(this.rewardForm.position),
      reward: String(this.rewardForm.reward),
    };

    const request$ = this.editingSeasonRewardId
      ? this.seasonService.updateSeasonReward(this.seasonId, this.editingSeasonRewardId, payload)
      : this.seasonService.createSeasonReward(this.seasonId, payload);

    request$.subscribe({
      next: () => {
        this.resetSeasonRewardForm();
        this.feedbackSuccess = 'Season reward salvo com sucesso.';
        this.loadAll();
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível salvar o season reward.');
      },
    });
  }

  editSeasonReward(reward: AdminSeasonReward): void {
    this.editingSeasonRewardId = reward.id;
    this.rewardForm = { ...reward };
  }

  deleteSeasonReward(rewardId: number): void {
    this.seasonService.deleteSeasonReward(this.seasonId, rewardId).subscribe({
      next: () => {
        this.feedbackSuccess = 'Season reward removido.';
        this.loadAll();
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível remover o season reward.');
      },
    });
  }

  saveLevel(): void {
    if (this.levelForm.level === undefined || this.levelForm.xp_to_unlock === undefined) {
      this.feedbackError = 'Informe nível e XP.';
      return;
    }

    const payload = {
      level: Number(this.levelForm.level),
      xp_to_unlock: Number(this.levelForm.xp_to_unlock),
    };

    const request$ = this.editingLevelId
      ? this.seasonService.updateLevel(this.seasonId, this.editingLevelId, payload)
      : this.seasonService.createLevel(this.seasonId, payload);

    request$.subscribe({
      next: () => {
        this.resetLevelForm();
        this.feedbackSuccess = 'Nível salvo com sucesso.';
        this.loadAll();
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível salvar o nível.');
      },
    });
  }

  editLevel(level: AdminBattlePassLevel): void {
    this.editingLevelId = level.id;
    this.levelForm = { ...level };
  }

  deleteLevel(levelId: number): void {
    this.seasonService.deleteLevel(this.seasonId, levelId).subscribe({
      next: () => {
        this.feedbackSuccess = 'Nível removido.';
        this.loadAll();
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível remover o nível.');
      },
    });
  }

  saveMission(): void {
    if (
      !this.missionForm.category ||
      !this.missionForm.name ||
      !this.missionForm.description ||
      !this.missionActivationLocal
    ) {
      this.feedbackError = 'Preencha os campos obrigatórios da missão.';
      return;
    }

    const payload = {
      category: String(this.missionForm.category),
      name: String(this.missionForm.name),
      description: String(this.missionForm.description),
      xp_reward: Number(this.missionForm.xp_reward || 0),
      activation_date: new Date(this.missionActivationLocal).toISOString(),
      target_value: Number(this.missionForm.target_value || 1),
      query: this.missionForm.query || null,
    };

    const request$ = this.editingMissionId
      ? this.seasonService.updateMission(this.seasonId, this.editingMissionId, payload)
      : this.seasonService.createMission(this.seasonId, payload);

    request$.subscribe({
      next: () => {
        this.resetMissionForm();
        this.feedbackSuccess = 'Missão salva com sucesso.';
        this.loadAll();
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível salvar a missão.');
      },
    });
  }

  editMission(mission: AdminBattlePassMission): void {
    this.editingMissionId = mission.id;
    this.missionForm = { ...mission };
    this.missionActivationLocal = this.toLocalDateInput(mission.activation_date);
  }

  deleteMission(missionId: number): void {
    this.seasonService.deleteMission(this.seasonId, missionId).subscribe({
      next: () => {
        this.feedbackSuccess = 'Missão removida.';
        this.loadAll();
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(error, 'Não foi possível remover a missão.');
      },
    });
  }

  savePassReward(): void {
    const payload = {
      level: Number(this.passRewardForm.level),
      reward_type: this.passRewardForm.reward_type,
      category: this.passRewardForm.category,
      reward_amount: Number(this.passRewardForm.reward_amount),
      hero_id: this.passRewardForm.hero_id,
    };

    const request$ = this.editingPassRewardId
      ? this.seasonService.updatePassReward(this.seasonId, this.editingPassRewardId, payload)
      : this.seasonService.createPassReward(this.seasonId, payload);

    request$.subscribe({
      next: () => {
        this.resetPassRewardForm();
        this.feedbackSuccess = 'Recompensa do passe salva com sucesso.';
        this.loadAll();
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(
          error,
          'Não foi possível salvar a recompensa do passe.'
        );
      },
    });
  }

  editPassReward(reward: AdminBattlePassReward): void {
    this.editingPassRewardId = reward.id;
    this.passRewardForm = {
      level: reward.level,
      reward_type: reward.reward_type,
      category: reward.category,
      reward_amount: reward.reward_amount,
      hero_id: null,
    };
  }

  deletePassReward(rewardId: number): void {
    this.seasonService.deletePassReward(this.seasonId, rewardId).subscribe({
      next: () => {
        this.feedbackSuccess = 'Recompensa do passe removida.';
        this.loadAll();
      },
      error: (error) => {
        this.feedbackError = this.extractErrorMessage(
          error,
          'Não foi possível remover a recompensa do passe.'
        );
      },
    });
  }

  onFileSelected(event: Event, field: 'decorator' | 'background' | 'preview' | 'modal'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    if (field === 'decorator') {
      this.passLevelDecoratorImage = file;
    }
    if (field === 'background') {
      this.passBackgroundImage = file;
    }
    if (field === 'preview') {
      this.passRewardsPreviewImage = file;
    }
    if (field === 'modal') {
      this.passBuyModalImage = file;
    }
  }

  toLocalDateInput(isoDate: string): string {
    const date = new Date(isoDate);
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = date.getFullYear();
    const mm = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());
    const hh = pad(date.getHours());
    const min = pad(date.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }

  resetSeasonRewardForm(): void {
    this.editingSeasonRewardId = undefined;
    this.rewardForm = { position: 1, reward: '' };
  }

  resetLevelForm(): void {
    this.editingLevelId = undefined;
    this.levelForm = { level: 0, xp_to_unlock: 0 };
  }

  resetMissionForm(): void {
    this.editingMissionId = undefined;
    this.missionForm = {
      category: 'BATTLE_VOLUME',
      name: '',
      description: '',
      xp_reward: 0,
      target_value: 1,
      query: '',
    };
    this.missionActivationLocal = '';
  }

  resetPassRewardForm(): void {
    this.editingPassRewardId = undefined;
    this.passRewardForm = {
      level: 0,
      reward_type: 'COIN',
      category: 'FREE',
      reward_amount: 0,
      hero_id: null,
    };
  }

  private extractErrorMessage(error: any, fallback: string): string {
    const response = error?.error;
    if (!response) {
      return fallback;
    }

    if (typeof response === 'string') {
      return response;
    }

    if (typeof response?.detail === 'string') {
      return response.detail;
    }

    const fieldMessages: string[] = [];
    Object.entries(response).forEach(([field, value]) => {
      if (Array.isArray(value)) {
        fieldMessages.push(`${field}: ${value.join(', ')}`);
      } else if (typeof value === 'string') {
        fieldMessages.push(`${field}: ${value}`);
      }
    });

    return fieldMessages.length > 0 ? fieldMessages.join(' | ') : fallback;
  }
}
